"use client";

import React from "react";
import { logToSentry } from "@/lib/PlatformData/platformDataErrorTracking";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";

interface Props {
  children: React.ReactNode;
  envConfig: EnvConfig;
  searchId: string;
}

interface State {
  hasError: boolean;
}

/**
 * Defence-in-depth boundary around EmbeddedMap. EmbeddedMap already catches the
 * mapbox-gl WebGL-init throw internally, but this guarantees that any future
 * unguarded throw from the map (e.g. WebGL unavailable) can never propagate to the
 * route-level src/app/error.tsx and take down the whole hotel page.
 */
class EmbeddedMapErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { envConfig, searchId } = this.props;

    logToSentry(error, [["component", "EmbeddedMap"]], {
      componentStack: errorInfo.componentStack,
    });

    sendKibanaLog({
      envConfig,
      message: "EmbeddedMap render error (WebGL unavailable)",
      category: "error",
      searchId,
      jsonMessage: { error: error.message },
    });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export default EmbeddedMapErrorBoundary;
